import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Pencil, Search, Trash2, Mail, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import EmailModal from '../../components/EmailModal/EmailModal';
import { successAlert, errorAlert, confirmDialog } from '../../utils/alerts';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [emailConsultant, setEmailConsultant] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/');
      setUsers(response.data.results || response.data);
    } catch (err) {
      toast.error('Erro ao carregar usuários.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) return;
    try {
      await api.delete(`/users/${id}/`);
      toast.success('Usuário removido com sucesso!');
      fetchUsers();
    } catch (err) {
      toast.error('Erro ao remover usuário.');
    }
  };

  const handleResendActivation = async (user) => {
    const confirmed = await confirmDialog({
      title: 'Reenviar e-mail de ativação?',
      text: `Um novo link de ativação será enviado para ${user.email || 'o e-mail cadastrado'}.`,
      confirmText: 'Reenviar',
      icon: 'question',
    });
    if (!confirmed) return;
    try {
      const response = await api.post(`/users/${user.id}/resend_activation/`);
      successAlert('E-mail reenviado', response.data?.detail || 'E-mail de ativação reenviado com sucesso.');
    } catch (err) {
      errorAlert('Erro ao reenviar', err.response?.data?.detail || 'Não foi possível reenviar o e-mail de ativação.');
    }
  };

  const roleLabel = (role) => {
    const labels = {
      admin_master: 'Administrador Master',
      gestor: 'Gestor',
      consultor: 'Consultor',
    };
    return labels[role] || role;
  };

  const filteredUsers = users.filter((u) =>
    u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar usuário..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          />
        </div>
        <Link
          to="/users/new"
          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus size={16} />
          Novo Usuário
        </Link>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Usuário</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">E-mail</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Papel</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-400 text-sm">
                    Nenhum usuário encontrado.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">
                        {user.first_name || user.last_name
                          ? `${user.first_name} ${user.last_name}`.trim()
                          : user.username}
                      </div>
                      <div className="text-gray-500 text-xs">@{user.username}</div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{user.email || '-'}</td>
                    <td className="px-6 py-4 text-gray-600">{roleLabel(user.role)}</td>
                    <td className="px-6 py-4">
                      {user.is_active === false ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                          Pendente de ativação
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                          Ativo
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-3">
                        {user.is_active === false && (
                          <button
                            onClick={() => handleResendActivation(user)}
                            className="text-amber-600 hover:text-amber-700"
                            title="Reenviar ativação"
                          >
                            <RefreshCw size={16} />
                          </button>
                        )}
                        {user.role === 'consultor' && user.email && (
                          <button
                            onClick={() => setEmailConsultant(user)}
                            className="text-blue-600 hover:text-blue-700"
                            title="Enviar e-mail"
                          >
                            <Mail size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/users/${user.id}/edit`)}
                          className="text-blue-600 hover:text-blue-700"
                          title="Editar"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="text-red-500 hover:text-red-700"
                          title="Excluir"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {emailConsultant && (
        <EmailModal
          consultant={emailConsultant}
          onClose={() => setEmailConsultant(null)}
          onSent={fetchUsers}
        />
      )}
    </div>
  );
};

export default UserList;
